import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useGroupFilter } from 'pared/Routes/renderer/groupFilter'

import { useDateFilter } from '../../dateFilter'
import calc from '../../utils/calc'
import { useVariables } from '../../variables'
import { IApiDataType } from '../types'

type IMetricDataType = {
  businessQuarter: number
  businessMonth: number
  businessWeek: number
  businessWeekOfMonth: number
  metricData: Record<
    string,
    {
      name: string
      unit: string
      value: number
    }
  >
  endDate: string
}

type IDataType =
  | {
      trendLocationGroupMetricValues: {
        nodes: IMetricDataType[]
      }
    }
  | {
      trendLocationMetricValues: {
        nodes: IMetricDataType[]
      }
    }

interface IReturnType {
  period: string
  value: number | null
}

const currentYearQuery = gql`
  query trendLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }

    trendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const priorYearQuery = gql`
  query trendPriorYearLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }

    trendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        endDate
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

export const wingitnorthSssChartsConfigs = {
  period: 'string',
  currentValue: 'percent',
  lastYearValue: 'percent',
  twoYearsValue: 'percent',
  twoYearsStackedValue: 'percent',
} as const

function getRevenueCenterName(code: string) {
  if (code.includes('revenue_center_')) {
    switch (code) {
      case 'revenue_center_sales_online_this_year':
        return 'Online (Pick Up)'

      case 'revenue_center_sales_takeoutcallincallcenter_this_year':
        return 'Take Out - Call in/Call Center'

      case 'revenue_center_sales_dispatch_this_year':
        return 'Online (Dispatch)'

      default:
        return code
          .replace(/revenue_center_sales_/g, '')
          .replace(/revenue_center_check_count_/g, '')
          .replace(/revenue_center_check_average_/g, '')
          .replace(/_comparable/g, '')
          .replace(/_([a-z])/g, (match, letter) => ` ${letter.toUpperCase()}`)
          .replace(/^[a-z]/, (letter) => letter.toUpperCase())
    }
  } else {
    return code
      .replace(/net_/g, '')
      .replace(/check_count/g, 'tickets')
      .replace(/_([a-z])/g, (match, letter) => ` ${letter.toUpperCase()}`)
      .replace(/^[a-z]/, (letter) => letter.toUpperCase())
  }
}

const useWingitnorthSssCharts = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()

  const groupBy = variables.farwestTimeinterval?.value[0][0] || 'business_month'

  const dateList = ((groupBy: string) => {
    if (groupBy === 'business_week')
      return variables.date?.getCalendar('week', 'year')?.ids || [[]]
    else if (groupBy === 'business_quarter') return ['Q1', 'Q2', 'Q3', 'Q4']
    else return variables.date?.getCalendar('period', 'year')?.ids || [[]]
  })(groupBy)

  const metrics = variables.selectOptions?.orderMode?.value[0][0]
  const iFilter = hasGroupBy
    ? {
        location_group_ids: groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        metrics: [`${metrics}_percentage_diff_to_last_year`],
      }
    : {
        location_ids: groupFilter?.ids,
        metrics: [`${metrics}_percentage_diff_to_last_year`],
      }

  const { data: currentData, loading: currentLoading } = useQuery<IDataType>(
    currentYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iGroupBy: groupBy,
        iFilter,
        hasGroupBy,
      },
      skip: !startDate || !endDate || !metrics,
    },
  )

  const { data: priorData, loading: priorLoading } = useQuery<IDataType>(
    priorYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iGroupBy: groupBy,
        iFilter: {
          ...iFilter,
          use_yoy: true,
        },
        hasGroupBy,
      },
      skip: !startDate || !endDate || !metrics,
    },
  )

  const loading = currentLoading || priorLoading

  return {
    data: useMemo((): IApiDataType => {
      const currentDataGroupBy = hasGroupBy
        ? currentData &&
          'trendLocationGroupMetricValues' in currentData &&
          currentData.trendLocationGroupMetricValues?.nodes
        : currentData &&
          'trendLocationMetricValues' in currentData &&
          currentData.trendLocationMetricValues?.nodes
      const priorDataGroupBy = hasGroupBy
        ? priorData &&
          'trendLocationGroupMetricValues' in priorData &&
          priorData.trendLocationGroupMetricValues?.nodes
        : priorData &&
          'trendLocationMetricValues' in priorData &&
          priorData.trendLocationMetricValues?.nodes

      if (!currentDataGroupBy || !priorDataGroupBy || !metrics) return null

      const metricData = currentDataGroupBy.map((item): IReturnType => {
        const periodEndDate = moment(item.endDate)
        const yesterday = moment().subtract(1, 'days')
        const period = ((groupBy: string) => {
          if (groupBy === 'business_week')
            return `P${item.businessMonth}W${item.businessWeekOfMonth}`
          else if (groupBy === 'business_quarter')
            return `Q${item.businessQuarter}`
          else return `P${item.businessMonth}`
        })(groupBy)

        return {
          period,
          value: yesterday.isAfter(periodEndDate)
            ? item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
                .value || null
            : null,
        }
      })

      const priorMetricData = priorDataGroupBy.map((item): IReturnType => {
        const period = ((groupBy: string) => {
          if (groupBy === 'business_week')
            return `P${item.businessMonth}W${item.businessWeekOfMonth}`
          else if (groupBy === 'business_quarter')
            return `Q${item.businessQuarter}`
          else return `P${item.businessMonth}`
        })(groupBy)

        return {
          period,
          value: item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
            .value
            ? item.metricData?.[`${metrics}_percentage_diff_to_last_year`].value
            : null,
        }
      })

      const displayMetric = getRevenueCenterName(metrics)

      return {
        source: dateList
          .map((date) => {
            const currentData = metricData.find((d) => d.period === date)
            const sameDaylyData = priorMetricData.find((d) => d.period === date)

            return {
              period: `${date} - Comp ${displayMetric}`,
              currentValue: currentData?.value,
              lastYearValue: sameDaylyData?.value,
              twoYearsStackedValue: calc(
                currentData?.value,
                '+',
                sameDaylyData?.value,
              ),
            }
          })
          .filter((d) => d.period),
      }
    }, [currentData, priorData, startDate, endDate]),
    loading,
  }
}

export default useWingitnorthSssCharts
